/**
 * Pure session store for strategy questionnaire (answers + cursor + mode).
 *
 * Story of answering:
 * 1. User highlights a choice → UpdateDraftAnswer (draft only)
 * 2. User confirms → AnswerQuestion + outcome (next question / stage done / session done)
 * 3. Leaving the screen is NOT stored here — the hook builds a Leave plan and navigates
 *
 * Belongs here: draft/committed maps, stage/question indices, sessionMode.
 * Does not belong here: navigation, HTTP, Rive, risk, leave plans, or advance rules.
 */
import type {
  StrategyQuestionnaireAnswersMap,
  StrategyQuestionnaireStage
} from '../../shared/types/questionnaire.types'
import { ClearAnswersOnEditMode } from '../../shared/types/questionnaire.types'
import { areAnswersEqual } from '../../question-utils/normalizeAnswersMap'
import { getAnswersAfterAdd, getAnswersAfterEdit } from './flowRules'
import { getEditStageIds } from '../../question-utils/stageEditScope'

/** Bump only when committed content actually changes. */
function nextCommittedAnswersVersion({
  previousAnswers,
  nextAnswers,
  previousVersion
}: {
  previousAnswers: StrategyQuestionnaireAnswersMap
  nextAnswers: StrategyQuestionnaireAnswersMap
  previousVersion: number
}): number {
  if (areAnswersEqual(previousAnswers, nextAnswers)) return previousVersion
  return previousVersion + 1
}

/**
 * Product-intent flag for the current answering session.
 *
 * Scope: session reducer + useStrategyQuestionnaire leave/finish only.
 * Not scope: which stages render — that comes from the route (`:stageId` filters
 * questionnaireData). Mode only answers “what should happen when answering is done?”
 *
 * Defaults to Questionnaire; StageEdit / HubSip are set when an edit path starts
 * and reset back to Questionnaire on leave, cancel, or exit.
 */
export const STRATEGY_SESSION_MODE = {
  /**
   * Full assessment (all stages, including SIP).
   * When: first run, hydration, “edit all” from review, or after leave/cancel reset.
   * Finish: go to strategy review (then resolve risk). Changed answers may clear risk.
   */
  Questionnaire: 'questionnaire',
  /**
   * One-stage edit from review (or similar) — same UI, filtered stages via edit route.
   * When: beginEditOne(stageId).
   * Finish: pop back (usually to review). Changed answers may clear risk.
   */
  StageEdit: 'stage_edit',
  /**
   * Hub “Monthly SIP” card only — same single-stage edit machinery, different finish policy.
   * When: openHubSipEdit() from hub.
   * Finish: POST SIP answer slice immediately, keep existing risk profile, return to hub.
   * No review / resolve.
   */
  HubSip: 'hub_sip'
} as const

export type StrategySessionMode =
  (typeof STRATEGY_SESSION_MODE)[keyof typeof STRATEGY_SESSION_MODE]

/**
 * What AnswerQuestion does after writing the answer into draft.
 * Chosen by the session hook from flowRules — not by the UI.
 */
export enum StrategyConfirmOutcome {
  /** Still in this stage — move cursor to another question. */
  NextQuestion = 'next_question',
  /** Last question of this stage — draft updated; cursor waits for goNextStage. */
  StageDone = 'stage_done',
  /** All done answering — draft and committed become the same map. */
  AllDone = 'all_done'
}

export type StrategyConfirmOutcomePayload =
  | {
      kind: StrategyConfirmOutcome.NextQuestion
      stageIndex: number
      questionIndex: number
    }
  | { kind: StrategyConfirmOutcome.StageDone }
  | { kind: StrategyConfirmOutcome.AllDone }

export enum StrategySessionActionType {
  /**
   * Full-flow hydrate gateway: set draft + committed (and cursor) from policy.
   * Replaces SeedFromPayloadIfEmpty — may wipe to {} when status is not done.
   */
  ApplyHydration = 'apply_hydration',
  /** Selection highlight only — updates draftAnswers; does not move or commit. */
  UpdateDraftAnswer = 'update_draft_answer',
  /** User confirmed this answer — updates draft (and committed if AllDone). */
  AnswerQuestion = 'answer_question',
  MoveCursor = 'move_cursor',
  ResetCursor = 'reset_cursor',
  ClearQuestionAnswer = 'clear_question_answer',
  DiscardDraft = 'discard_draft',
  PrepareStageEdit = 'prepare_stage_edit',
  BeginEditAll = 'begin_edit_all',
  SetSessionMode = 'set_session_mode',
  /** Hub resume — user opened full strategy review at least once this session. */
  MarkReachedReview = 'mark_reached_review',
  /** After successful POST — align to the answersVersion that was actually submitted. */
  MarkSubmittedAnswers = 'mark_submitted_answers'
}

export type StrategySessionState = {
  /** Answers the UI shows while the user is still answering. */
  draftAnswers: StrategyQuestionnaireAnswersMap
  /** Last finished / seeded answers (review + resolve / hub POST source of truth). */
  committedAnswers: StrategyQuestionnaireAnswersMap
  stageIndex: number
  questionIndex: number
  sessionMode: StrategySessionMode
  /** Hub resume: after full review once, open review instead of questionnaire. */
  hasReachedReviewOnce: boolean
  /** Increments when committed answer content changes. */
  committedAnswersVersion: number
  /** answersVersion from the last successful POST (in-flight-safe). */
  lastSubmittedAnswersVersion: number
}

export const initialStrategySessionState: StrategySessionState = {
  draftAnswers: {},
  committedAnswers: {},
  stageIndex: 0,
  questionIndex: 0,
  sessionMode: STRATEGY_SESSION_MODE.Questionnaire,
  hasReachedReviewOnce: false,
  committedAnswersVersion: 0,
  lastSubmittedAnswersVersion: 0
}

export type StrategySessionAction =
  | {
      type: StrategySessionActionType.ApplyHydration
      draftAnswers: StrategyQuestionnaireAnswersMap
      committedAnswers: StrategyQuestionnaireAnswersMap
      /** When true, park cursor at stage 0 / question 0. */
      resetCursorToStart: boolean
      /** Wipe → false; seed → true; omit to leave unchanged. */
      hasReachedReviewOnce?: boolean
      /**
       * Version policy from hydration plan — do not infer from empty maps.
       * wipe → 0/0; seed → bump if content changed, then align submitted.
       */
      answersVersionPolicy: 'wipe' | 'seed'
    }
  | {
      type: StrategySessionActionType.UpdateDraftAnswer
      questionId: string
      value: string | number
      stages: StrategyQuestionnaireStage[]
    }
  | {
      type: StrategySessionActionType.AnswerQuestion
      questionId: string
      value: string | number
      stages: StrategyQuestionnaireStage[]
      outcome: StrategyConfirmOutcomePayload
    }
  | {
      type: StrategySessionActionType.MoveCursor
      stageIndex: number
      questionIndex: number
    }
  | { type: StrategySessionActionType.ResetCursor }
  | { type: StrategySessionActionType.ClearQuestionAnswer; questionId: string }
  | { type: StrategySessionActionType.DiscardDraft }
  | {
      type: StrategySessionActionType.PrepareStageEdit
      stageId: string
      stages: StrategyQuestionnaireStage[]
      sessionMode: StrategySessionMode
    }
  | {
      type: StrategySessionActionType.BeginEditAll
      stages: StrategyQuestionnaireStage[]
    }
  | { type: StrategySessionActionType.SetSessionMode; mode: StrategySessionMode }
  | { type: StrategySessionActionType.MarkReachedReview }
  | {
      type: StrategySessionActionType.MarkSubmittedAnswers
      lastSubmittedAnswersVersion: number
    }

/** Pure reducer — no navigation, no HTTP, no animations. */
export function strategySessionReducer(
  state: StrategySessionState,
  action: StrategySessionAction
): StrategySessionState {
  switch (action.type) {
    case StrategySessionActionType.ApplyHydration: {
      // Full-flow gateway only — edit routes use PrepareStageEdit
      if (action.answersVersionPolicy === 'wipe') {
        return {
          ...state,
          draftAnswers: action.draftAnswers,
          committedAnswers: action.committedAnswers,
          committedAnswersVersion: 0,
          lastSubmittedAnswersVersion: 0,
          ...(action.resetCursorToStart ? { stageIndex: 0, questionIndex: 0 } : {}),
          ...(action.hasReachedReviewOnce !== undefined
            ? { hasReachedReviewOnce: action.hasReachedReviewOnce }
            : {})
        }
      }

      // seed — server answers are already submitted
      const committedAnswersVersion = nextCommittedAnswersVersion({
        previousAnswers: state.committedAnswers,
        nextAnswers: action.committedAnswers,
        previousVersion: state.committedAnswersVersion
      })
      return {
        ...state,
        draftAnswers: action.draftAnswers,
        committedAnswers: action.committedAnswers,
        committedAnswersVersion,
        lastSubmittedAnswersVersion: committedAnswersVersion,
        ...(action.resetCursorToStart ? { stageIndex: 0, questionIndex: 0 } : {}),
        ...(action.hasReachedReviewOnce !== undefined
          ? { hasReachedReviewOnce: action.hasReachedReviewOnce }
          : {})
      }
    }

    case StrategySessionActionType.UpdateDraftAnswer: {
      const nextDraftAnswers = getAnswersAfterAdd(
        state.draftAnswers,
        action.questionId,
        action.value,
        action.stages
      )
      return { ...state, draftAnswers: nextDraftAnswers }
    }

    case StrategySessionActionType.AnswerQuestion: {
      const nextDraftAnswers = getAnswersAfterAdd(
        state.draftAnswers,
        action.questionId,
        action.value,
        action.stages
      )
      const { outcome } = action

      if (outcome.kind === StrategyConfirmOutcome.NextQuestion) {
        return {
          ...state,
          draftAnswers: nextDraftAnswers,
          stageIndex: outcome.stageIndex,
          questionIndex: outcome.questionIndex
        }
      }

      if (outcome.kind === StrategyConfirmOutcome.StageDone) {
        /**
         * Keep the cursor on the finished stage.
         * Page-flow plays the stage animation, then calls goNextStage.
         * Moving cursor here would flash the next stage under the animation.
         */
        return { ...state, draftAnswers: nextDraftAnswers }
      }

      /**
       * AllDone: one map becomes both draft and committed.
       * Do not reset cursor to 0 — that flashed the first stage before leave navigated away.
       */
      const committedAnswers = { ...nextDraftAnswers }
      return {
        ...state,
        draftAnswers: committedAnswers,
        committedAnswers,
        committedAnswersVersion: nextCommittedAnswersVersion({
          previousAnswers: state.committedAnswers,
          nextAnswers: committedAnswers,
          previousVersion: state.committedAnswersVersion
        })
      }
    }

    case StrategySessionActionType.MoveCursor:
      return {
        ...state,
        stageIndex: action.stageIndex,
        questionIndex: action.questionIndex
      }

    case StrategySessionActionType.ResetCursor:
      return { ...state, stageIndex: 0, questionIndex: 0 }

    case StrategySessionActionType.ClearQuestionAnswer: {
      // Draft only — committed map unchanged until AnswerQuestion / AllDone
      const nextDraftAnswers = { ...state.draftAnswers }
      delete nextDraftAnswers[action.questionId]
      return { ...state, draftAnswers: nextDraftAnswers }
    }

    case StrategySessionActionType.DiscardDraft:
      // Revert highlight edits; committed answers stay as last finished map
      return {
        ...state,
        draftAnswers: { ...state.committedAnswers }
      }

    case StrategySessionActionType.PrepareStageEdit: {
      // Clear edited stage (+ dependents) from committed into a fresh draft
      const stageIdsToClear = getEditStageIds(action.stageId)
      let nextDraftAnswers = state.committedAnswers
      for (const stageIdToClear of stageIdsToClear) {
        nextDraftAnswers = getAnswersAfterEdit(
          nextDraftAnswers,
          ClearAnswersOnEditMode.CLEAR_CURRENT_STAGE,
          { stageId: stageIdToClear },
          action.stages
        )
      }
      return {
        ...state,
        draftAnswers: nextDraftAnswers,
        // stageIndex 0 = first stage in the hook's filtered edit set, not necessarily API stage 0
        stageIndex: 0,
        questionIndex: 0,
        sessionMode: action.sessionMode
      }
    }

    case StrategySessionActionType.BeginEditAll:
      // Draft only — restart full questionnaire from stage 0; committed stays until AllDone
      return {
        ...state,
        sessionMode: STRATEGY_SESSION_MODE.Questionnaire,
        draftAnswers: getAnswersAfterEdit(
          state.committedAnswers,
          ClearAnswersOnEditMode.CLEAR_ALL_STAGES,
          undefined,
          action.stages
        ),
        stageIndex: 0,
        questionIndex: 0
      }

    case StrategySessionActionType.SetSessionMode:
      return { ...state, sessionMode: action.mode }

    case StrategySessionActionType.MarkReachedReview:
      if (state.hasReachedReviewOnce) return state
      return { ...state, hasReachedReviewOnce: true }

    case StrategySessionActionType.MarkSubmittedAnswers:
      return {
        ...state,
        lastSubmittedAnswersVersion: action.lastSubmittedAnswersVersion
      }

    default:
      return state
  }
}
